import React, { useCallback, useMemo, useState } from "react";
import {
  createEditor,
  Descendant,
  Editor,
  Transforms,
  Text,
  BaseEditor,
} from "slate";
import {
  Slate,
  Editable,
  withReact,
  RenderLeafProps,
  ReactEditor,
} from "slate-react";
import Picker from "@emoji-mart/react";
import data from "@emoji-mart/data";

type CustomText = {
  text: string;
  bold?: boolean;
  italic?: boolean;
  strikethrough?: boolean;
  code?: boolean;
};

type CustomElement = {
  type: "paragraph";
  children: CustomText[];
};

declare module "slate" {
  interface CustomTypes {
    Editor: BaseEditor & ReactEditor;
    Element: CustomElement;
    Text: CustomText;
  }
}

const isMarkActive = (editor: Editor, format: keyof CustomText): boolean => {
  const [match] = Editor.nodes(editor, {
    match: (n) => Text.isText(n) && n[format] === true,
    universal: true,
  });
  return !!match;
};

const toggleMark = (editor: Editor, format: keyof CustomText): void => {
  const active = isMarkActive(editor, format);
  if (active) Editor.removeMark(editor, format);
  else Editor.addMark(editor, format, true);
};

interface ButtonProps {
  editor: Editor;
  name: keyof CustomText;
  onMouseDown: (e: React.MouseEvent<HTMLButtonElement>) => void;
  children: React.ReactNode;
}

const Button: React.FC<ButtonProps> = ({ editor, name, onMouseDown, children }) => {
  const active = isMarkActive(editor, name);

  return (
    <button
      type="button"
      onMouseDown={(e) => {
        e.preventDefault();
        onMouseDown(e);
      }}
      style={{
        marginRight: 4,
        border: "1px solid #ddd",
        borderRadius: 6,
        background: active ? "gray" : "#fff",
        color: active ? "white" : "black",
        cursor: "pointer",
      }}
    >
      {children}
    </button>
  );
};

interface ToolbarProps {
  editor: Editor;
  onToggle: (format: keyof CustomText) => void;
  setShowPicker: React.Dispatch<React.SetStateAction<boolean>>;
}

const Toolbar: React.FC<ToolbarProps> = ({ editor, onToggle, setShowPicker }) => {
  return (
    <div className="flex mb-4 mt-4">
      <Button editor={editor} name="bold" onMouseDown={() => onToggle("bold")}>
        B
      </Button>
      <Button editor={editor} name="italic" onMouseDown={() => onToggle("italic")}>
        I
      </Button>
      <Button
        editor={editor}
        name="strikethrough"
        onMouseDown={() => onToggle("strikethrough")}
      >
        S
      </Button>
      <Button editor={editor} name="code" onMouseDown={() => onToggle("code")}>
        {"</>"}
      </Button>
      <Button
        editor={editor}
        name="bold"
        onMouseDown={() => setShowPicker((v) => !v)}
      >
        😀
      </Button>
    </div>
  );
};

function serializeToMeta(nodes: Descendant[]): string {
  const walk = (node: Descendant): string => {
    if (Text.isText(node)) {
      let txt = node.text;
      if (node.code) txt = "```" + txt + "```";
      if (node.bold) txt = "*" + txt + "*";
      if (node.italic) txt = "_" + txt + "_";
      if (node.strikethrough) txt = "~" + txt + "~";
      return txt;
    }

    const element = node as CustomElement;

    if (element.type === "paragraph") {
      return element.children.map(walk).join("") + "\n";
    }

    return element.children.map(walk).join("");
  };

  return nodes.map(walk).join("").replace(/\n+$/, "");
}

interface MetaTextareaProps {
  initialValue?: Descendant[];
  onChangePlain?: (plain: string) => void;
}

export const MetaTextarea: React.FC<MetaTextareaProps> = ({
  initialValue,
  onChangePlain,
}) => {
  const editor = useMemo(() => withReact(createEditor()), []);
  const [value, setValue] = useState<Descendant[]>(
    initialValue || [{ type: "paragraph", children: [{ text: "" }] }]
  );
  const [showPicker, setShowPicker] = useState(false);

  const onToggle = useCallback(
    (format: keyof CustomText) => toggleMark(editor, format),
    [editor]
  );

  const insertEmoji = useCallback(
    (emojiData: { native?: string; colons?: string }) => {
      const emoji = emojiData.native || emojiData.colons || "";
      Transforms.insertText(editor, emoji);
    },
    [editor]
  );

  const handleChange = useCallback(
    (nodes: Descendant[]): void => {
      setValue(nodes);
      if (onChangePlain) onChangePlain(serializeToMeta(nodes));
    },
    [onChangePlain]
  );

  const renderLeaf = useCallback(
    ({ attributes, children, leaf }: RenderLeafProps) => {
      const customLeaf = leaf as CustomText;
      if (customLeaf.bold) children = <strong>{children}</strong>;
      if (customLeaf.italic) children = <em>{children}</em>;
      if (customLeaf.strikethrough) children = <s>{children}</s>;
      if (customLeaf.code)
        children = (
          <code
            style={{
              background: "#f5f5f5",
              padding: "2px 4px",
              borderRadius: 4,
            }}
          >
            {children}
          </code>
        );
      return <span {...attributes}>{children}</span>;
    },
    []
  );

  return (
    <div>
      <Slate editor={editor} initialValue={value} onChange={handleChange}>
        <Toolbar editor={editor} onToggle={onToggle} setShowPicker={setShowPicker} />

        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div
            style={{
              flex: 1,
              border: "1px solid #e6e6e6",
              padding: 10,
              borderRadius: 6,
            }}
          >
            <Editable
              placeholder="Escreva a mensagem..."
              renderLeaf={renderLeaf}
              onKeyDown={(event) => {
                if ((event.ctrlKey || event.metaKey) && event.key === "b") {
                  event.preventDefault();
                  toggleMark(editor, "bold");
                }
              }}
            />
          </div>
        </div>

        {showPicker && (
          <div style={{ position: "relative", marginTop: 8 }}>
            <div style={{ position: "absolute", zIndex: 50 }}>
              <Picker data={data} onEmojiSelect={insertEmoji} />
            </div>
          </div>
        )}
      </Slate>

      <div style={{ marginTop: 8 }}>
        <small>Preview Meta-text:</small>
        <pre
          style={{
            whiteSpace: "pre-wrap",
            background: "#fafafa",
            padding: 8,
          }}
        >
          {serializeToMeta(value)}
        </pre>
      </div>
    </div>
  );
};

export default MetaTextarea;
